import React, { useState, useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { observer } from 'mobx-react-lite'

// evertel
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { LogoutService } from '@evertel/session/LogoutService'
import { AppService } from '@evertel/web/core'
import { DrawerController } from '@evertel/drawer'
import { Drawer, LeftGutter } from '@evertel/web/drawer'
import { Header } from '@evertel/web/header'
import { ChangePasswordModal } from '@evertel/web/settings'
import { AppLoader, Col, useUI } from '@evertel/web/ui'
import { GPCPushNotification, PushService } from '@evertel/push'
import { LoginService } from '@evertel/session/LoginService'
import { InviteActionHandler } from '@evertel/web/invites'
import { ResetPasswordController } from '@evertel/blue-user'
import { useJoinRoom } from '@evertel/web/room'

// Failsafe: initAuthorized() should always settle, but if a request hangs
// we fail-open after this delay rather than blocking the app forever
// (mirrors mobile's fail-open init: Promise.allSettled + .finally).
const INIT_FAILSAFE_MS = 10000

const AuthorizedLayout: React.FC = observer(() => {
    const { addToast, showAlertBar, toggleLeftNav } = useUI()

    const navigate = useNavigate()
    const location = useLocation()

    const appService = useService(AppService)
    const pushService = useService(PushService)
    const sessionService = useService(SessionService, [])
    const logoutService = useService(LogoutService, [])
    const loginService = useService(LoginService, [])
    const inviteActionHandler = useService(InviteActionHandler)
    const resetPasswordController = useService(ResetPasswordController, [])
    const drawerController = useService(DrawerController, [])
    const { openJoinRoom } = useJoinRoom()

    const callbackExecutingRef = useRef(false)

    const [isInitialized, setIsInitialized] = useState(false)
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false)
    const [isCrisisPending, setIsCrisisPending] = useState(() => {
        if (localStorage.getItem('crisis')) {
            localStorage.removeItem('crisis')
            return true
        }
        return false
    })

    // Runs only on initial render - catches direct navigation to protected routes without auth
    useState(() => {
        if (!sessionService.currentUserId) {
            logoutService.logout('AuthorizedLayout_backup_logout')
        }
    })

    useEffect(() => {
        if (location.pathname === '/' && sessionService.currentUserId) {
            navigate('/landing', { replace: true })
        }
    }, [location, sessionService.currentUserId])

    // Authorized init/cleanup - mirrors mobile AuthorizedLayout calling appService
    useEffect(() => {
        if (!sessionService.currentUserId) return

        const failsafe = setTimeout(() => setIsInitialized(true), INIT_FAILSAFE_MS)

        // .finally: fail-open like mobile - a failed fetchUserDetails shouldn't
        // block the app forever; the 25s user poll self-heals the stores.
        appService.initAuthorized().finally(() => {
            clearTimeout(failsafe)
            setIsInitialized(true)
        })

        // Web-specific: push notification click routing (needs useNavigate)
        const notificationClicked = (notification: GPCPushNotification) => {
            const { type, roomId, repliesToId, threadId, auditType } = notification.data

            let path = '/'

            if (type === 'RoomMessage') path = repliesToId ? `/room/${roomId}/replies/${repliesToId}` : `/room/${roomId}`
            if (type === 'ThreadMessage') path = `/thread/${threadId}`
            if (type === 'AuditComplete') path = auditType === 'room_export' ? `/room/${roomId}` : `/manage/audits`

            navigate(path)
        }

        pushService.addNotificationClickedListener(notificationClicked)

        resetPasswordController.init(sessionService.currentUserId)

        return () => {
            clearTimeout(failsafe)
            appService.destroyAuthorized()
            pushService.removeNotificationClickedListener(notificationClicked)
        }
    }, [sessionService.currentUserId])

    // -----------------------------------------------
    // Push notification permission prompt (web-only, independent).
    // Not part of the startup action priority chain - fires
    // regardless of password expiry or pending invites.
    // Mobile handles this on explicit user action instead.
    // -----------------------------------------------
    useEffect(() => {
        if (!sessionService.currentUserId || !isInitialized) return

        pushService.promptForNotificationPermission().then(() => {
            if (pushService.notificationPermission !== 'granted' && !localStorage.getItem('notPerm')) {
                showAlertBar({ type: pushService.notificationPermission, isOpen: true })
            }
        })
    }, [sessionService.currentUserId, isInitialized])

    // -----------------------------------------------
    // Startup actions - reactive, mirrors mobile AuthorizedLayout.
    // Re-evaluates when dependencies change. Only the first matching
    // branch runs. e.g. if password is near expiry, modal opens and
    // invite is skipped. Once user changes password, nearExpiration
    // flips false, effect re-runs, and falls through to invite.
    // -----------------------------------------------
    useEffect(() => {
        if (!sessionService.currentUserId || !isInitialized) return

        // 1. Password expiry takes priority
        if (resetPasswordController.nearExpiration) {
            setIsChangePasswordModalOpen(true)

        // 2. Login callback (e.g., registration continuation)
        } else if (loginService.onLoginCallback && !callbackExecutingRef.current) {
            callbackExecutingRef.current = true

            requestAnimationFrame(() => {
                loginService.onLoginCallback()
                loginService.setOnLoginCallback(undefined)
                callbackExecutingRef.current = false
            })

        // 3. Crisis mode — open public room browser (fires regardless of startupActionsDisabled)
        } else if (isCrisisPending) {
            setIsCrisisPending(false)
            openJoinRoom('public')

        // 4. Normal startup actions (only if not disabled by login flow)
        } else if (!loginService.startupActionsDisabled) {
            if (sessionService.pendingInviteToken) {
                sessionService.setCallbackUrl(null) // Invite directs to a specific location
                inviteActionHandler.consumePendingAction(navigate, addToast)

            // 5. Callback URL — navigate to the route the user attempted before login
            } else if (sessionService.callbackUrl) {
                const url = sessionService.consumeCallbackUrl()
                if (url) navigate(url)
            }
        }
    }, [
        sessionService.currentUserId,
        isInitialized,
        isCrisisPending,
        resetPasswordController.nearExpiration,
        loginService.onLoginCallback,
        loginService.startupActionsDisabled,
        sessionService.pendingInviteToken,
        sessionService.callbackUrl
    ])

    // Update history on location changes
    useEffect(() => {
        sessionService.updateNavigationHistory({
            pathname: location.pathname,
            params: location.search || ''
        })
    }, [location, sessionService])

    // The documents section has no drawer content: keep the left nav closed
    // while inside it; the cleanup restores it when leaving the section
    useEffect(() => {
        if (drawerController.activeSection !== 'everdocs') return

        toggleLeftNav('close')
        return () => toggleLeftNav('open')
    }, [drawerController.activeSection])

    // Set the zoomed state and close left nav when zoomed
    useEffect(() => {
        const handleSiteZoom = () => {
            const zoomLevel = window.devicePixelRatio
            if (zoomLevel >= 4) {
                toggleLeftNav('close')
                document.documentElement.classList.add('zoomed')
            } else {
                // if this is on it will open the menu every time the screen is resized.
                // toggleLeftNav('open')
                document.documentElement.classList.remove('zoomed')
            }
        }

        handleSiteZoom()
        window.addEventListener('resize', handleSiteZoom)
        return () => window.removeEventListener('resize', handleSiteZoom)
    }, [])


    if (!sessionService.currentUserId) return null

    return (
        <div className="app-wrap">
            <LeftGutter />
            <Drawer />
            <Col valign="top" className="canvas">
                <Header />
                <div className="page">
                    <div className="inset-round-corner"></div>
                    {/* Don't render routes until initAuthorized() has settled:
                        route components (e.g. RoomGate) make membership/permission
                        decisions from stores hydrated by fetchUserDetails() */}
                    {isInitialized ? <Outlet /> : <AppLoader isOpen type="full-overlay" />}
                </div>
            </Col>
            <div id="slide-panel-root"></div>

            {/* App-wide modals triggered by startup actions */}
            <ChangePasswordModal
                isOpen={isChangePasswordModalOpen}
                onClose={() => {
                    setIsChangePasswordModalOpen(false)
                    resetPasswordController.dismissNearExpiration()
                }}
            />
        </div>
    )
})

export default AuthorizedLayout
